import { FocusMonitor } from '@angular/cdk/a11y';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, HostBinding, ViewEncapsulation } from '@angular/core';
import { ANIMATION_MODULE_TYPE, Component, ElementRef, Inject, Input, NgZone, Optional } from '@angular/core';
import { MatButtonComponent } from '@shared/material/button/button.component';

/**
 * Material design anchor button.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: `a[app-mat-button], a[app-mat-raised-button], a[app-mat-icon-button],
             a[app-mat-stroked-button], a[app-mat-flat-button]`,
  exportAs: 'matButton, matAnchor',
  templateUrl: 'button.component.html',
  styleUrls: ['button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatAnchorComponent extends MatButtonComponent implements AfterViewInit, OnDestroy {
  /** Tabindex of the button. */
  @Input() tabIndex: number;

  // Note that we ignore the user-specified tabindex when it's disabled for
  // consistency with the `mat-button` applied on native buttons where even
  // though they have an index, they're not tabbable.
  @HostBinding('attr.tabindex')
  get hostTabIndex() {
    return this.disabled ? -1 : this.tabIndex;
  }

  @HostBinding('attr.aria-disabled')
  get ariaDisabled() {
    return this.disabled.toString();
  }

  constructor(
    focusMonitor: FocusMonitor,
    elementRef: ElementRef,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) animationMode: string,
    private _ngZone: NgZone,
  ) {
    super(elementRef, focusMonitor, animationMode);
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this._ngZone.runOutsideAngular(() => {
      this._elementRef.nativeElement.addEventListener('click', this._haltDisabledEvents);
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this._elementRef.nativeElement.removeEventListener('click', this._haltDisabledEvents);
  }

  _haltDisabledEvents = (event: Event): void => {
    // A disabled button shouldn't apply any actions
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };
}
